import { SwipeGamesItem } from '../model/swipe';
import getCurrentGameSelected from '../lib/currentGameSelected';
import eventType from '../enums/eventType';
import eventEmitters from '../lib/eventEmitters';

export class GameIconManager {
  private static readonly MAX_VISIBLE_ICONS = 5;
  private static readonly HAMBURGER_THRESHOLD = 5;
  private static readonly SCROLL_DELAY_MS = 30;
  private static readonly SCROLL_OFFSET_PX = 30;

  private readonly container: HTMLElement | null;
  private readonly icons: SwipeGamesItem[] = [];
  private activeIconIndex = -1;
  private onIconClick?: (icon: SwipeGamesItem, index: number) => void;

  constructor(container: HTMLElement | null, icons: SwipeGamesItem[]) {
    this.container = container;
    this.icons = icons;
  }

  public setOnIconClick(
    callback: (icon: SwipeGamesItem, index: number) => void,
  ): void {
    this.onIconClick = callback;
  }

  public getActiveIconIndex(): number {
    return this.activeIconIndex;
  }

  public setActiveIconIndex(index: number): void {
    this.activeIconIndex = index;
  }

  public setDefaultActiveIcon(): void {
    if (this.activeIconIndex !== -1 || this.icons.length === 0) return;

    const currentGameId = getCurrentGameSelected().currentGameId;

    if (currentGameId) {
      const gameIndex = this.icons.findIndex(
        (icon) => icon.gameId?.toString() === currentGameId,
      );
      this.activeIconIndex = gameIndex === -1 ? 0 : gameIndex;
    } else {
      this.activeIconIndex = 0;
    }
  }

  public renderAllIcons(): void {
    if (!this.container) return;

    this.container.innerHTML = '';
    let index = 0;
    for (const icon of this.icons) {
      const img = this.createIconElement(icon, index);
      this.container.appendChild(img);
      index++;
    }
  }

  private createIconElement(
    icon: SwipeGamesItem,
    index: number,
  ): HTMLImageElement {
    const img = document.createElement('img');
    img.src = icon?.gameIconUrl || '';
    img.alt = icon.displayName || 'Game';
    img.dataset.iconIndex = index.toString();
    img.classList.add('game-icon', 'game-icon--static');

    if (icon.gameId) {
      img.id = `game-icon-${icon.gameId}`;
    }

    img.onclick = () => this.handleIconClick(icon, index);
    img.onerror = () => eventEmitters(eventType.SWIPE_ERROR);

    return img;
  }

  private handleIconClick(icon: SwipeGamesItem, index: number): void {
    this.onIconClick?.(icon, index);
  }

  public updateIconVisibility(): void {
    if (!this.container) return;

    const allIcons = this.container.querySelectorAll('.game-icon, .promo-icon');

    if (this.icons.length > GameIconManager.HAMBURGER_THRESHOLD) {
      this.updateActiveStatesForAllIcons(allIcons);
    } else {
      this.updateIconVisibilityWithLimit(allIcons);
    }
  }

  private updateIconVisibilityWithLimit(allIcons: NodeListOf<Element>): void {
    let visibleCount = 0;
    for (const element of Array.from(allIcons)) {
      const iconIndex = Number.parseInt(
        (element as HTMLElement).dataset.iconIndex || '0',
      );

      visibleCount = this.processIconVisibility(
        element as HTMLElement,
        iconIndex,
        visibleCount,
      );
    }
  }

  private processIconVisibility(
    element: HTMLElement,
    iconIndex: number,
    visibleCount: number,
  ): number {
    if (this.shouldReplaceWithPromo(iconIndex)) {
      this.replaceIconWithPromo(element, iconIndex);
      return visibleCount + 1;
    }

    if (element.classList.contains('promo-icon')) {
      this.restoreIconFromPromo(element, iconIndex);
    }

    return this.updateIconVisibilityState(element, visibleCount);
  }

  private updateIconVisibilityState(
    element: HTMLElement,
    visibleCount: number,
  ): number {
    if (this.shouldHideIcon(0, visibleCount)) {
      this.hideIcon(element);
      return visibleCount;
    }

    this.showIcon(element);
    return visibleCount + 1;
  }

  private shouldReplaceWithPromo(iconIndex: number): boolean {
    return iconIndex === this.activeIconIndex;
  }

  private replaceIconWithPromo(element: HTMLElement, iconIndex: number): void {
    const activeIcon = this.icons[iconIndex];
    if (!activeIcon) return;

    const promoImageUrl = activeIcon.gameDropdownUrl || activeIcon.gameIconUrl;

    if (element.classList.contains('promo-icon')) {
      this.updateExistingPromoIcon(
        element as HTMLImageElement,
        activeIcon,
        promoImageUrl,
      );
      return;
    }

    const promoImg = this.createPromoElement(
      activeIcon,
      iconIndex,
      promoImageUrl,
    );
    promoImg.onerror = element.onerror;
    element.parentNode?.replaceChild(promoImg, element);
  }

  private updateExistingPromoIcon(
    img: HTMLImageElement,
    activeIcon: SwipeGamesItem,
    promoImageUrl: string | undefined,
  ): void {
    if (img.src !== promoImageUrl) {
      img.src = promoImageUrl || '';
    }
    img.alt = activeIcon.displayName || 'Selected Game';
    img.style.display = '';
    img.style.pointerEvents = 'auto';
    img.classList.add('active');
    this.applyGradientBackground(img, activeIcon);
  }

  private createPromoElement(
    activeIcon: SwipeGamesItem,
    iconIndex: number,
    promoImageUrl: string | undefined,
  ): HTMLImageElement {
    const promoImg = document.createElement('img');
    promoImg.src = promoImageUrl || '';
    promoImg.alt = activeIcon.displayName || 'Selected Game';
    promoImg.dataset.iconIndex = iconIndex.toString();
    promoImg.classList.add('promo-icon', 'active');
    promoImg.style.pointerEvents = 'auto';
    promoImg.decoding = 'async';
    promoImg.loading = 'eager';

    this.applyGradientBackground(promoImg, activeIcon);
    promoImg.onclick = () => this.handleIconClick(activeIcon, iconIndex);

    return promoImg;
  }

  private applyGradientBackground(
    element: HTMLElement,
    icon: SwipeGamesItem,
  ): void {
    const colors = icon.colorGradient;
    if (!colors?.length) return;

    let gradient: string;

    if (colors.length === 1) {
      gradient = colors[0];
    } else if (colors[0].includes('deg')) {
      gradient = `linear-gradient(${colors[0]}, ${colors.slice(1).join(', ')})`;
    } else {
      gradient = `linear-gradient(to bottom, ${colors.join(', ')})`;
    }

    element.style.background = gradient;
  }

  private restoreIconFromPromo(element: HTMLElement, iconIndex: number): void {
    const icon = this.icons[iconIndex];
    if (!icon) return;

    const gameImg = document.createElement('img');
    gameImg.src = icon.gameIconUrl || '';
    gameImg.alt = icon.displayName || 'Game';

    if (icon.gameId) gameImg.id = `game-icon-${icon.gameId}`;

    gameImg.dataset.iconIndex = iconIndex.toString();
    gameImg.classList.add('game-icon', 'game-icon--static');
    gameImg.classList.remove('active');

    gameImg.style.background = '';

    gameImg.onclick = () => this.handleIconClick(icon, iconIndex);
    gameImg.onerror = () => {
      eventEmitters(eventType.SWIPE_ERROR);
    };

    if (element.parentNode) {
      element.parentNode.replaceChild(gameImg, element);
    }
  }

  private updateActiveStatesForAllIcons(allIcons: NodeListOf<Element>): void {
    for (const element of Array.from(allIcons)) {
      const iconIndex = Number.parseInt(
        (element as HTMLElement).dataset.iconIndex || '0',
      );

      if (iconIndex === this.activeIconIndex) {
        this.replaceIconWithPromo(element as HTMLElement, iconIndex);
      } else {
        if (element.classList.contains('promo-icon')) {
          this.restoreIconFromPromo(element as HTMLElement, iconIndex);
        } else {
          this.showIconStatic(element as HTMLElement);
        }
        element.classList.remove('active');
      }
    }
  }

  private showIconStatic(img: HTMLElement): void {
    img.style.display = '';
    img.style.pointerEvents = 'auto';
    img.classList.remove('game-icon--hidden', 'game-icon--visible');
    img.classList.add('game-icon--static');
  }

  private shouldHideIcon(iconIndex: number, visibleCount: number): boolean {
    return visibleCount >= GameIconManager.MAX_VISIBLE_ICONS;
  }

  private hideIcon(img: HTMLElement): void {
    img.style.display = 'none';
    img.style.pointerEvents = 'none';
    img.classList.remove('game-icon--visible', 'game-icon--static');
    img.classList.add('game-icon--hidden');
  }

  private showIcon(img: HTMLElement): void {
    img.style.display = '';
    img.style.pointerEvents = 'auto';
    img.classList.remove('game-icon--hidden', 'game-icon--static');
    img.classList.add('game-icon--visible');
  }

  public scrollIconIntoView(index: number): void {
    if (!this.container) return;

    const iconElement = this.container.querySelector(
      `[data-icon-index="${index}"]`,
    ) as HTMLElement;

    if (!iconElement) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const isLastIcon = index === this.icons.length - 1;
        const isSecondLastIcon = index === this.icons.length - 2;

        if (isLastIcon || isSecondLastIcon) {
          this.scrollEdgeIcon(iconElement, isLastIcon);
        } else {
          this.scrollCenterIcon(iconElement);
        }
      });
    });
  }

  private scrollEdgeIcon(iconElement: HTMLElement, isLastIcon: boolean): void {
    setTimeout(() => {
      if (!this.container) return;

      if (isLastIcon) {
        const maxScrollLeft =
          this.container.scrollWidth - this.container.clientWidth;
        this.container.scrollTo({
          left: maxScrollLeft + GameIconManager.SCROLL_OFFSET_PX,
          behavior: 'smooth',
        });
      } else {
        const targetScrollLeft =
          this.calculateCenterScrollLeft(iconElement) +
          GameIconManager.SCROLL_OFFSET_PX;
        this.container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
      }
    }, GameIconManager.SCROLL_DELAY_MS);
  }

  private scrollCenterIcon(iconElement: HTMLElement): void {
    if (!this.container) return;

    const targetScrollLeft = this.calculateCenterScrollLeft(iconElement);
    this.container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  }

  private calculateCenterScrollLeft(iconElement: HTMLElement): number {
    if (!this.container) return 0;

    const containerWidth = this.container.offsetWidth;
    const iconOffsetLeft = iconElement.offsetLeft;
    const iconWidth = iconElement.offsetWidth;

    return iconOffsetLeft - containerWidth / 2 + iconWidth / 2;
  }

  public getActiveIcon(): SwipeGamesItem | null {
    if (this.isValidIndex(this.activeIconIndex)) {
      return this.icons[this.activeIconIndex];
    }
    return null;
  }

  public isValidIndex(index: number): boolean {
    return index >= 0 && index < this.icons.length;
  }

  public shouldShowHamburgerMenu(): boolean {
    return this.icons.length > GameIconManager.HAMBURGER_THRESHOLD;
  }
}
